import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Image } from 'components/core/Image';
import dropdownDealIcon from 'assets/images/icons/dropdown-deal-icon.svg';
import dropdownRightIcon from 'assets/images/icons/dropdown-right-icon.svg';
import dropdownDownArrowIcon from 'assets/images/icons/dropdown-down-arrow-icon.svg';

import './DealsDropdown.scss';

const Dropdown = ({ options = [], value = null, onChange, placeholder = 'Select an option', className = null, disabled = false }) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef(null);

	const selectedOption = options.find((option) => option.value === value);
	const selectedLabel = selectedOption ? selectedOption.display : placeholder;

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const handleOutsideClick = (event) => {
			if (ref.current && !ref.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleOutsideClick);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen]);

	return (
		<div className={classnames('dropdown-molecule', className, { disabled })} ref={ref}>
			<button
				type="button"
				className="dropdown-molecule__toggle"
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				onClick={() => !disabled && setIsOpen((open) => !open)}
			>
				<div className="dropdown-molecule__deal-header">
					<Image src={dropdownDealIcon} className="dropdown-molecule__deal-icon" alt="dropdown deal icon" />
					<span className={classnames('dropdown-molecule__selected', { 'dropdown-molecule__selected--placeholder': !selectedOption })}>
						{selectedLabel}
					</span>
				</div>

				<div className={classnames('dropdown-molecule__icon', { open: isOpen })}>
					<Image src={dropdownDownArrowIcon} className="dropdown-molecule__arrow-img" alt="dropdown down arrow icon" />
				</div>
			</button>
			{isOpen && (
				<div className="dropdown-molecule__menu" role="listbox">
					{options.map((option) => (
						<button
							type="button"
							key={option.value}
							role="option"
							aria-selected={option.value === value}
							className={classnames('dropdown-molecule__item', {
								selected: option.value === value,
							})}
							onClick={() => {
								if (disabled) {
									return;
								}
								onChange(option.value);
								setIsOpen(false);
							}}
						>
							<span>{option.display}</span>
							{option.value === value && <span className="dropdown-molecule__checkmark"><Image src={dropdownRightIcon} className="dropdown-molecule__checkmark-icon" alt="dropdown right icon" /></span>}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

Dropdown.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.any.isRequired,
			display: PropTypes.node.isRequired,
		})
	),
	value: PropTypes.any,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	className: PropTypes.string,
	disabled: PropTypes.bool,
};

export default Dropdown;
